import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import css from './FilterSidebar.module.css';
import FilterSectionHeader from './FilterSectionHeader';
import SearchInput from './SearchInput';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { isArrayLength } from '../../../util/genericHelpers';
import { serviceOptions } from '../../../config/configSearch';
import { PARAM_KEYS, TalentOfferingFilterTabs } from '../../../util/enums';
import Icons from '../../../components/Icons/Icons';

const ServiceFilterSection = ({ resetAll, listingCategory }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();

  // State for search term, selected options, and collapsed state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubOptions, setSelectedSubOptions] = useState({
    autographsSubTypes: [],
    shoutoutsSubTypes: [],
    endorsementsSubTypes: [],
    creativeMediaSubTypes: [],
    appearancesSubTypes: [],
    enhancedServicesSubTypes: [],
    unlockableTypes: [],
  });
  const [collapsed, setCollapsed] = useState({});

  // Category to sub-option mapping
  const categoryKeys = {
    isAutograph: 'autographsSubTypes',
    isShoutout: 'shoutoutsSubTypes',
    isEndorsed: 'endorsementsSubTypes',
    isCreativeMedia: 'creativeMediaSubTypes',
    isAppearance: 'appearancesSubTypes',
    isExclusiveTraining: 'enhancedServicesSubTypes',
    isMentorship: 'enhancedServicesSubTypes',
    isBusinessDevelopment: 'enhancedServicesSubTypes',
    isUnlockableContent: 'unlockableTypes',
    isTalentPlusServices: 'unlockableTypes',
  };

  // Initialize state from URL parameters on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const initialCategories = [];
    const initialSubOptions = {
      autographsSubTypes: [],
      shoutoutsSubTypes: [],
      endorsementsSubTypes: [],
      creativeMediaSubTypes: [],
      appearancesSubTypes: [],
      enhancedServicesSubTypes: [],
      unlockableTypes: [],
    };

    searchParams.forEach((value, key) => {
      if (key.startsWith('pub_')) {
        const paramKey = key.replace('pub_', '');
        if (value === 'true' && Object.keys(categoryKeys).includes(paramKey)) {
          initialCategories.push(paramKey);
        } else if (Object.values(categoryKeys).includes(paramKey)) {
          initialSubOptions[paramKey] = value.split(',').filter(v => v);
        }
      }
    });

    setSelectedCategories(initialCategories);
    setSelectedSubOptions(initialSubOptions);
  }, [location.search]);

  // Helper function to update URL parameters
  const updateMultipleURLParams = paramsObj => {
    const searchParams = new URLSearchParams(location.search);
    // Clear all filter-related parameters
    const filterParams = [
      ...Object.keys(categoryKeys).map(key => `pub_${key}`),
      ...Object.values(categoryKeys).map(key => `pub_${key}`),
    ];
    filterParams.forEach(param => searchParams.delete(param));

    // Set new parameters from paramsObj
    Object.keys(paramsObj).forEach(key => {
      const values = paramsObj[key];
      if (values && values.length > 0) {
        searchParams.set(key, values.join(','));
      }
    });

    // Always ensure pub_listingCategory=talent is included
    searchParams.set(PARAM_KEYS.ListingCategory, listingCategory);
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Handle main category checkbox change
  const handleCategoryChange = (categoryValue, isChecked) => {
    const updatedCategories = isChecked
      ? [...selectedCategories, categoryValue]
      : selectedCategories.filter(c => c !== categoryValue);

    // Clear sub-options for deselected category
    const updatedSubOptions = { ...selectedSubOptions };
    if (!isChecked) {
      const subTypeKey = categoryKeys[categoryValue];
      if (subTypeKey) {
        updatedSubOptions[subTypeKey] = [];
      }
    }

    // Build URL parameters
    const params = {};
    updatedCategories.forEach(category => {
      params[`pub_${category}`] = ['true'];
    });
    Object.entries(updatedSubOptions).forEach(([key, values]) => {
      if (values.length > 0) {
        params[`pub_${key}`] = values;
      }
    });

    setSelectedCategories(updatedCategories);
    setSelectedSubOptions(updatedSubOptions);
    updateMultipleURLParams(params);
  };

  // Handle nested sub-option checkbox change
  const handleSubOptionChange = (categoryValue, subOptionValue, isChecked) => {
    const subTypeKey = categoryKeys[categoryValue];
    if (!subTypeKey) return;

    const updatedSubOptions = {
      ...selectedSubOptions,
      [subTypeKey]: isChecked
        ? [...selectedSubOptions[subTypeKey], subOptionValue]
        : selectedSubOptions[subTypeKey].filter(val => val !== subOptionValue),
    };

    // Ensure the main category is selected
    const updatedCategories = selectedCategories.includes(categoryValue)
      ? selectedCategories
      : [...selectedCategories, categoryValue];

    // Build URL parameters
    const params = {};
    updatedCategories.forEach(category => {
      params[`pub_${category}`] = ['true'];
    });
    Object.entries(updatedSubOptions).forEach(([key, values]) => {
      if (values.length > 0) {
        params[`pub_${key}`] = values;
      }
    });

    setSelectedCategories(updatedCategories);
    setSelectedSubOptions(updatedSubOptions);
    updateMultipleURLParams(params);
  };

  // Toggle collapse/expand for nested options
  const toggleCollapse = categoryValue => {
    setCollapsed(prev => ({
      ...prev,
      [categoryValue]: !prev[categoryValue],
    }));
  };

  // Handle clear filter
  const handleClear = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubOptions({
      autographsSubTypes: [],
      shoutoutsSubTypes: [],
      endorsementsSubTypes: [],
      creativeMediaSubTypes: [],
      appearancesSubTypes: [],
      enhancedServicesSubTypes: [],
      unlockableTypes: [],
    });
    setCollapsed({});
    updateMultipleURLParams({});
    resetAll();
  };

  // Filter service options based on search term
  const filteredOptions = serviceOptions
    .filter(option => {
      const mainLabel = intl
        .formatMessage({
          id: option.labelId,
          defaultMessage: option.defaultMessage,
        })
        .toLowerCase();
      const mainLabelMatches = mainLabel.includes(searchTerm.toLowerCase());

      const matchingSubOptions = option.subOptions.filter(subOption => {
        if (!subOption.labelId) {
          return mainLabelMatches;
        }
        const subLabel = intl
          .formatMessage({
            id: subOption.labelId,
            defaultMessage: subOption.defaultMessage,
          })
          .toLowerCase();
        return subLabel.includes(searchTerm.toLowerCase());
      });

      return mainLabelMatches || matchingSubOptions.length > 0;
    })
    .map(option => {
      const mainLabel = intl
        .formatMessage({
          id: option.labelId,
          defaultMessage: option.defaultMessage,
        })
        .toLowerCase();

      const subOptionsToShow = mainLabel.includes(searchTerm.toLowerCase())
        ? option.subOptions
        : option.subOptions.filter(subOption => {
            if (!subOption.labelId) {
              return true;
            }
            const subLabel = intl
              .formatMessage({
                id: subOption.labelId,
                defaultMessage: subOption.defaultMessage,
              })
              .toLowerCase();
            return subLabel.includes(searchTerm.toLowerCase());
          });

      return {
        ...option,
        subOptions: subOptionsToShow,
      };
    });

  return (
    <div className={css.filterSection}>
      <FilterSectionHeader titleId="FilterCategories.serviceOffered" handleClear={handleClear} />

      <div className={css.searchContainer}>
        <SearchInput
          placeholder="Search Services..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className={css.searchInput}
        />
      </div>

      <div className={css.optionsScrollContainer}>
        {filteredOptions.length > 0 ? (
          <div className={css.optionsList}>
            {filteredOptions.map((option, index) => (
              <div key={index} className={css.serviceGroup}>
                <div className={css.serviceGroupHeader}>
                  {option.section === TalentOfferingFilterTabs.TALENT_PLUS && (
                    <label className={css.checkboxContainer}>
                      <input
                        type="checkbox"
                        className={css.checkbox}
                        checked={selectedCategories.includes('isTalentPlusServices')}
                        onChange={e =>
                          handleCategoryChange('isTalentPlusServices', e.target.checked)
                        }
                      />
                      <span className={css.checkmark}></span>
                    </label>
                  )}
                  <h4 className={css.serviceGroupTitle}>
                    <FormattedMessage id={option.labelId} defaultMessage={option.defaultMessage} />
                  </h4>

                  {isArrayLength(option.subOptions) && (
                    <button
                      className={`${css.expandButton} ${
                        collapsed[option.section] ? css.collapsed : css.expanded
                      }`}
                      onClick={() => toggleCollapse(option.section)}
                      aria-expanded={!collapsed[option.section]}
                      aria-label={collapsed[option.section] ? 'Expand options' : 'Collapse options'}
                    >
                      <svg
                        className={css.expandIcon}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M8 4.5L3 9.5h10L8 4.5z" />
                      </svg>
                    </button>
                  )}
                </div>

                {!collapsed[option.section] && isArrayLength(option.subOptions) && (
                  <div className={css.serviceOptions}>
                    {option.subOptions.map((subOption, subIndex) => (
                      <div key={subIndex} className={css.optionItem}>
                        <label className={css.checkboxContainer}>
                          <input
                            type="checkbox"
                            className={css.checkbox}
                            checked={selectedCategories.includes(subOption.value)}
                            onChange={e => handleCategoryChange(subOption.value, e.target.checked)}
                          />
                          <span className={css.checkmark}></span>
                          <span className={css.checkboxLabel}>
                            <FormattedMessage
                              id={subOption.labelId}
                              defaultMessage={subOption.defaultMessage}
                            />
                          </span>
                        </label>

                        {selectedCategories.includes(subOption.value) && subOption.nestedOptions && (
                          <div className={css.nestedOptionsContainer}>
                            {subOption.nestedOptions.map((nestedOption, nestedIndex) => (
                              <label key={nestedIndex} className={css.nestedCheckboxContainer}>
                                <input
                                  type="checkbox"
                                  className={css.nestedCheckbox}
                                  checked={selectedSubOptions[
                                    categoryKeys[subOption.value]
                                  ]?.includes(nestedOption.value)}
                                  onChange={e =>
                                    handleSubOptionChange(
                                      subOption.value,
                                      nestedOption.value,
                                      e.target.checked
                                    )
                                  }
                                />
                                <span className={css.nestedCheckmark}></span>
                                <span className={css.nestedLabel}>{nestedOption.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={css.noResultsContainer}>
            <div className={css.noResultsIcon}>
              <Icons name="noResults" />
            </div>
            <p className={css.noResultsText}>
              <FormattedMessage
                id="FilterCategories.noResults"
                defaultMessage="No matching services found"
              />
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceFilterSection;
